import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { announcementsApi, holidaysApi, widgetsApi } from "../widgetsApi";

/** The three dashboard cards in one call (both views). */
/** scope = { companyId?, branchId? } (HR dashboard); My view passes none. */
export const useDashboardWidgets = ({ enabled = true, scope } = {}) =>
  useQuery({
    queryKey: [
      "widgets",
      "all",
      scope?.companyId || null,
      scope?.branchId || null,
    ],
    queryFn: () => widgetsApi.all(scope),
    staleTime: 5 * 60 * 1000,
    enabled,
  });

/** Today's attendance numbers + who is on leave (HR dashboard). */
export const useAttendanceToday = (params, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["dashboard", "attendanceToday", params],
    queryFn: () => widgetsApi.attendanceToday(params),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    enabled,
  });

export const useNotCheckedIn = (params, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["dashboard", "notCheckedIn", params],
    queryFn: () => widgetsApi.notCheckedIn(params),
    placeholderData: keepPreviousData,
    enabled,
  });

/** Every birthday in the next `days` (the cards only get 5). */
export const useBirthdays = (days = 30, { enabled = true, scope } = {}) =>
  useQuery({
    queryKey: [
      "widgets",
      "birthdays",
      days,
      scope?.companyId || null,
      scope?.branchId || null,
    ],
    queryFn: () => widgetsApi.birthdays({ days, ...scope }),
    staleTime: 10 * 60 * 1000,
    enabled,
  });

const useWriteMutation = (fn, keys) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      [...keys, ["widgets"]].forEach((queryKey) =>
        qc.invalidateQueries({ queryKey }),
      ),
  });
};

/* ---------- Announcements ---------- */
export const useAnnouncements = (params) =>
  useQuery({
    queryKey: ["announcements", "list", params],
    queryFn: () => announcementsApi.list(params),
    placeholderData: keepPreviousData,
  });
const ANN = [["announcements"]];
export const useSaveAnnouncement = () =>
  useWriteMutation(
    ({ id, body }) =>
      id ? announcementsApi.update(id, body) : announcementsApi.create(body),
    ANN,
  );
export const useDeleteAnnouncement = () =>
  useWriteMutation((id) => announcementsApi.remove(id), ANN);

/* ---------- Holidays ---------- */
export const useHolidays = (params) =>
  useQuery({
    queryKey: ["holidays", "manage", params],
    queryFn: () => holidaysApi.manage(params),
    placeholderData: keepPreviousData,
  });
const HOL = [["holidays"]];
export const useSaveHoliday = () =>
  useWriteMutation(
    ({ id, body }) =>
      id ? holidaysApi.update(id, body) : holidaysApi.create(body),
    HOL,
  );
export const useDeleteHoliday = () =>
  useWriteMutation((id) => holidaysApi.remove(id), HOL);
export const useCopyHolidayYear = () =>
  useWriteMutation((body) => holidaysApi.copyYear(body), HOL);
